<script lang="ts" setup>
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'

const props = defineProps<{
	title: string
	url: string
}>()

const emits = defineEmits(['copied'])

const encodedTitle = encodeURIComponent(props.title)
const encodedUrl = encodeURIComponent(props.url)

const linkedInShareUrl = computed(() => `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`)
const whatsAppShareUrl = computed(() => `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`)
const emailShareUrl = computed(() => `mailto:?subject=${props.title}&body=${props.url}`)
const facebookShareUrl = computed(() => `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`)
const instagramShareUrl = computed(() => `https://www.instagram.com/share?url=${props.url}`)
const xShareUrl = computed(() => `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`)

function copyLink() {
	navigator.clipboard.writeText(props.url)
	emits('copied')
}
</script>
<template>
	<Menu as="div" class="relative inline-block text-left">
		<div>
			<MenuButton as="div" class="flex items-center justify-center gap-1 font-semibold text-gray-900">
				<Icon
					name="ph:share-network"
					class="size-5 flex-none text-gray-500  hover:text-green-500"
					aria-hidden="true"
					data-test="share-button" />
			</MenuButton>
		</div>
		<transition
			enter-active-class="transition ease-out duration-100"
			enter-from-class="transform opacity-0 scale-95"
			enter-to-class="transform opacity-100 scale-100"
			leave-active-class="transition ease-in duration-75"
			leave-from-class="transform opacity-100 scale-100"
			leave-to-class="transform opacity-0 scale-95">
			<MenuItems
				class="absolute left-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none">
				<div class="py-1">
					<MenuItem v-slot="{ active }">
						<a
							href="#"
							:class="[
								active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
								'block px-4 py-2 text-sm no-underline',
							]"
							data-test="share-copy-link"
							@click="copyLink">
							<Icon name="ph:link-duotone" class="inline-block mr-2 size-5 icon-fix" /> Copy Link
						</a>
					</MenuItem>
					<MenuItem v-slot="{ active }">
						<a
							:href="linkedInShareUrl"
							target="_blank"
							:class="[
								active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
								'block px-4 py-2 text-sm no-underline',
							]"
							data-test="share-linkedin">
							<Icon name="ph:linkedin-logo-duotone" class="inline-block mr-2 size-5 icon-fix" /> LinkedIn
						</a>
					</MenuItem>
					<MenuItem v-slot="{ active }">
						<a
							:href="whatsAppShareUrl"
							target="_blank"
							:class="[
								active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
								'block px-4 py-2 text-sm no-underline',
							]"
							data-test="share-whatsapp">
							<Icon name="ph:whatsapp-logo-duotone" class="inline-block mr-2 size-5 icon-fix" /> WhatsApp
						</a>
					</MenuItem>
					<MenuItem v-slot="{ active }">
						<a
							:href="emailShareUrl"
							:class="[
								active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
								'block px-4 py-2 text-sm no-underline',
							]"
							data-test="share-email">
							<Icon name="ph:envelope-duotone" class="inline-block mr-2 size-5 icon-fix" /> Email
						</a>
					</MenuItem>
					<MenuItem v-slot="{ active }">
						<a
							:href="facebookShareUrl"
							target="_blank"
							:class="[
								active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
								'block px-4 py-2 text-sm no-underline',
							]"
							data-test="share-facebook">
							<Icon name="ph:facebook-logo-duotone" class="inline-block mr-2 size-5 icon-fix" /> Facebook
						</a>
					</MenuItem>
					<MenuItem v-slot="{ active }">
						<a
							:href="instagramShareUrl"
							target="_blank"
							:class="[
								active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
								'block px-4 py-2 text-sm no-underline',
							]"
							data-test="share-instagram">
							<Icon name="ph:instagram-logo-duotone" class="inline-block mr-2 size-5 icon-fix" />
							Instagram
						</a>
					</MenuItem>
					<MenuItem v-slot="{ active }">
						<a
							:href="xShareUrl"
							target="_blank"
							:class="[
								active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
								'block px-4 py-2 text-sm no-underline',
							]"
							data-test="share-x">
							<Icon name="ph:x-logo-duotone" class="inline-block mr-2 size-5 icon-fix" /> X
						</a>
					</MenuItem>
				</div>
			</MenuItems>
		</transition>
	</Menu>
</template>
